import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, XCircle, ClipboardList, ListChecks, Trophy, Target, Info, Play } from 'lucide-react'
import { getTestForTaking, skipTest, submitTest, type SubmitResult, type TakeTest } from '@/services/testService'
import applyBg from '@/pages/JobApplication/assets/apply-bg.png'

interface Props {
  testId: number
  kind: 'PRE' | 'POST'
  // ปิดหน้าทดสอบกลับไปเรียน — ผู้เรียกโหลดสถานะแบบทดสอบใหม่เอง
  onDone: () => void
}

export default function TestRunner({ testId, kind, onDone }: Props) {
  const [test, setTest] = useState<TakeTest | null>(null)
  const [answers, setAnswers] = useState<Record<number, number>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<SubmitResult | null>(null)
  const [started, setStarted] = useState(false)   // false = หน้าชี้แจงก่อนเริ่ม, true = กำลังทำข้อสอบ

  // ทำใหม่ (ปุ่ม "ทำแบบทดสอบอีกครั้ง") — โหลดโจทย์ชุดเดิมอีกครั้งและล้างคำตอบ
  const reload = useCallback(async () => {
    setLoading(true)
    setError('')
    setResult(null)
    setAnswers({})
    setStarted(true)   // ทำซ้ำ: ข้ามหน้าชี้แจง เข้าทำข้อสอบเลย
    try {
      setTest(await getTestForTaking(testId))
    } catch (err) {
      setTest(null)
      setError(err instanceof Error ? err.message : 'โหลดแบบทดสอบไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [testId])

  // โหลดตอนเปิดหน้า (ผู้เรียกใส่ key={testId} จึงเริ่มด้วย loading=true เสมอ)
  useEffect(() => {
    let cancelled = false
    getTestForTaking(testId)
      .then((t) => { if (!cancelled) { setTest(t); setLoading(false) } })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'โหลดแบบทดสอบไม่สำเร็จ')
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [testId])

  const answered = test ? test.questions.filter((q) => answers[q.question_id] !== undefined).length : 0
  const total = test ? test.questions.length : 0
  const isPre = kind === 'PRE'

  const submit = async () => {
    if (!test || answered < total || submitting) return
    setSubmitting(true)
    setError('')
    try {
      setResult(await submitTest(test.test_id, answers))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ส่งคำตอบไม่สำเร็จ')
    } finally {
      setSubmitting(false)
    }
  }

  const skip = async () => {
    if (!test || submitting) return
    setSubmitting(true)
    setError('')
    try {
      await skipTest(test.test_id)
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ข้ามแบบทดสอบไม่สำเร็จ')
      setSubmitting(false)
    }
  }

  if (loading) {
    return <div className="flex flex-1 items-center justify-center bg-white text-sm text-[#69738A]">กำลังโหลดแบบทดสอบ...</div>
  }

  if (!test) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 bg-white p-6 text-center">
        <p className="text-sm text-red-600">{error || 'ไม่พบแบบทดสอบ'}</p>
        <button onClick={onDone} className="rounded-none bg-accent px-5 py-2 text-sm font-medium text-white hover:bg-accent2">กลับไปเรียน</button>
      </div>
    )
  }

  // ── ผลการสอบ ────────────────────────────────────────────────────────────
  if (result) {
    const passed = result.passed === true
    return (
      <div className="relative isolate flex flex-1 items-center justify-center overflow-auto bg-[#F8F7FC] p-6">
        <img src={applyBg} alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 h-full w-full object-cover" />
        <div className="relative w-full max-w-lg rounded-none border border-[#E7E5F0] bg-[#FCFBFF] p-8 text-center">
          {isPre ? (
            <ClipboardList className="mx-auto mb-4 h-12 w-12 text-accent" strokeWidth={1.5} />
          ) : passed ? (
            <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-emerald-500" strokeWidth={1.5} />
          ) : (
            <XCircle className="mx-auto mb-4 h-12 w-12 text-amber-500" strokeWidth={1.5} />
          )}
          <h2 className="mb-1 text-xl font-semibold text-[#19172A]">
            {isPre ? 'ทำแบบทดสอบก่อนเรียนเสร็จแล้ว' : passed ? 'ผ่านแบบทดสอบหลังเรียน' : 'ยังไม่ผ่านเกณฑ์'}
          </h2>
          <p className="mb-1 text-3xl font-bold text-accent">{result.score} / {result.max_score}</p>
          <p className="mb-4 text-sm text-[#69738A]">คิดเป็น {result.percent}%</p>
          {!isPre && !passed && (
            <p className="mb-5 text-sm text-[#69738A]">ต้องได้อย่างน้อย {result.pass_percent}% จึงจะผ่าน — ทบทวนบทเรียนแล้วลองทำใหม่ได้</p>
          )}
          {!isPre && passed && (
            <p className="mb-5 text-sm text-[#69738A]">
              {result.course_completed ? 'คุณเรียนจบคอร์สนี้แล้ว' : 'ผ่านเกณฑ์แล้ว — เรียนให้ครบทุกบทเพื่อจบคอร์ส'}
            </p>
          )}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {!isPre && !passed && (
              <button onClick={reload} className="rounded-none bg-accent px-5 py-2 text-sm font-medium text-white hover:bg-accent2">ทำแบบทดสอบอีกครั้ง</button>
            )}
            {!isPre && passed && result.course_completed && (
              <Link to="/account?tab=certificates" className="rounded-none border border-accent/40 px-5 py-2 text-sm font-medium text-accent hover:bg-accent/5">ดูใบประกาศนียบัตร</Link>
            )}
            <button
              onClick={onDone}
              className={`rounded-none px-5 py-2 text-sm font-medium ${!isPre && !passed ? 'border border-[#E7E5F0] text-[#69738A] hover:text-[#19172A]' : 'bg-accent text-white hover:bg-accent2'}`}
            >
              {isPre ? 'เริ่มเรียน' : 'กลับไปที่บทเรียน'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── หน้าชี้แจงก่อนเริ่มทำ ────────────────────────────────────────────────
  if (!started) {
    const rules = isPre
      ? [
          'ใช้วัดความรู้พื้นฐานก่อนเรียน ไม่มีผลต่อการจบคอร์สและการได้ใบประกาศนียบัตร',
          'ทำได้ครั้งเดียว — หรือจะข้ามไปเรียนก่อนก็ได้ (ข้ามแล้วจะไม่ถามอีก)',
          'ตอบให้ครบทุกข้อก่อนกดส่งคำตอบ แต่ละข้อเลือกได้คำตอบเดียว',
          'เมื่อส่งแล้วจะเห็นคะแนนรวม ระบบไม่เฉลยคำตอบรายข้อ',
        ]
      : [
          `ต้องได้คะแนนอย่างน้อย ${test.pass_percent ?? '-'}% จึงจะผ่านและจบคอร์ส`,
          'ทำซ้ำได้ไม่จำกัดครั้งจนกว่าจะผ่าน — เมื่อผ่านแล้วจะทำซ้ำไม่ได้',
          'ตอบให้ครบทุกข้อก่อนกดส่งคำตอบ แต่ละข้อเลือกได้คำตอบเดียว',
          'ไม่มีการจับเวลา แต่ระบบไม่เฉลยคำตอบรายข้อ ควรทบทวนบทเรียนให้ครบก่อนทำ',
        ]
    return (
      <div className="relative isolate flex flex-1 items-center justify-center overflow-auto bg-[#F8F7FC] p-6">
        {/* พื้นหลัง logistics แบบจางๆ — ภาพเดียวกับหน้า In-house/สมัครงาน */}
        <img src={applyBg} alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 h-full w-full object-cover" />
        <div className="relative w-full max-w-2xl overflow-hidden rounded-none border border-[#E7E5F0] bg-white shadow-[0_8px_30px_rgba(108,99,255,0.08)]">
          <div className="border-b border-[#E7E5F0] bg-gradient-to-br from-[#F3F1FF] to-white px-8 py-7">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-accent">
              {isPre ? 'Pre-test · แบบทดสอบก่อนเรียน' : 'Post-test · แบบทดสอบหลังเรียน'}
            </p>
            <h1 className="text-2xl font-semibold leading-snug text-[#19172A]">{test.title}</h1>
          </div>

          <div className="grid grid-cols-3 divide-x divide-[#E7E5F0] border-b border-[#E7E5F0]">
            <div className="px-4 py-5 text-center">
              <ListChecks className="mx-auto mb-1.5 h-5 w-5 text-accent" strokeWidth={1.75} />
              <p className="text-xl font-semibold text-[#19172A]">{total}</p>
              <p className="text-xs text-[#69738A]">จำนวนข้อ</p>
            </div>
            <div className="px-4 py-5 text-center">
              <Trophy className="mx-auto mb-1.5 h-5 w-5 text-accent" strokeWidth={1.75} />
              <p className="text-xl font-semibold text-[#19172A]">{test.max_score}</p>
              <p className="text-xs text-[#69738A]">คะแนนเต็ม</p>
            </div>
            <div className="px-4 py-5 text-center">
              <Target className="mx-auto mb-1.5 h-5 w-5 text-accent" strokeWidth={1.75} />
              <p className="text-xl font-semibold text-[#19172A]">{isPre ? 'ไม่มี' : `${test.pass_percent}%`}</p>
              <p className="text-xs text-[#69738A]">เกณฑ์ผ่าน</p>
            </div>
          </div>

          <div className="px-8 py-6">
            <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#19172A]">
              <Info className="h-4 w-4 text-accent" strokeWidth={1.75} /> ข้อควรทราบก่อนเริ่ม
            </p>
            <ul className="space-y-2.5">
              {rules.map((rule) => (
                <li key={rule} className="flex items-start gap-2.5 text-sm leading-relaxed text-[#4A5468]">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" strokeWidth={1.75} />
                  <span>{rule}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-[#E7E5F0] bg-[#FCFBFF] px-8 py-4">
            {error && <span className="mr-auto text-sm text-red-600">{error}</span>}
            {isPre && (
              <button
                onClick={skip}
                disabled={submitting}
                className="rounded-none border border-[#E7E5F0] px-4 py-2 text-sm text-[#69738A] transition hover:text-[#19172A] disabled:opacity-50"
              >
                ข้ามไปก่อน
              </button>
            )}
            <button
              onClick={() => setStarted(true)}
              disabled={total === 0}
              className="flex items-center gap-2 rounded-none bg-accent px-6 py-2.5 text-sm font-medium text-white transition hover:bg-accent2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Play className="h-4 w-4 fill-white" /> เริ่มทำแบบทดสอบ
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── ทำแบบทดสอบ ──────────────────────────────────────────────────────────
  const progressPct = total ? Math.round((answered / total) * 100) : 0
  const LETTERS = ['ก', 'ข', 'ค', 'ง', 'จ', 'ฉ']
  return (
    <div className="flex flex-1 flex-col overflow-hidden bg-[#F8F7FC]">
      <div className="flex-1 overflow-auto p-6 md:p-10">
        <div className="mx-auto" style={{ maxWidth: 800 }}>
          {/* หัวข้อ + แถบความคืบหน้า + ปุ่มเลขข้อ (กดเพื่อเลื่อนไปข้อนั้น) */}
          <div className="mb-6 rounded-none border border-[#E7E5F0] bg-white p-6 shadow-[0_2px_10px_rgba(108,99,255,0.05)]">
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-accent">
              {isPre ? 'Pre-test' : 'Post-test'}
            </p>
            <h1 className="mb-4 text-2xl font-semibold text-[#19172A]">{test.title}</h1>
            <div className="mb-3 flex items-center gap-3">
              <div className="h-1.5 flex-1 overflow-hidden rounded-none bg-[#E7E5F0]">
                <div className="h-full rounded-none bg-accent transition-all duration-300" style={{ width: `${progressPct}%` }} />
              </div>
              <span className="w-10 shrink-0 text-right text-sm font-semibold tabular-nums text-accent">{progressPct}%</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {test.questions.map((q, i) => {
                const done = answers[q.question_id] !== undefined
                return (
                  <a
                    key={q.question_id}
                    href={`#question-${q.question_id}`}
                    className={`flex h-7 w-7 items-center justify-center rounded-none text-xs font-medium transition ${done ? 'bg-accent text-white' : 'border border-[#E7E5F0] text-[#69738A] hover:border-accent/40'}`}
                  >
                    {i + 1}
                  </a>
                )
              })}
            </div>
          </div>

          <div className="space-y-5">
            {test.questions.map((q, index) => (
              <div key={q.question_id} id={`question-${q.question_id}`} className="scroll-mt-6 rounded-none border border-[#E7E5F0] bg-white p-6 shadow-[0_2px_10px_rgba(108,99,255,0.05)]">
                <div className="mb-4 flex items-start gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent/10 text-sm font-semibold text-accent">{index + 1}</span>
                  <p className="pt-0.5 text-[15px] font-medium leading-relaxed text-[#19172A]">{q.question_text}</p>
                </div>
                <div className="space-y-2.5 pl-10">
                  {q.options.map((option, optionIndex) => {
                    const selected = answers[q.question_id] === optionIndex
                    return (
                      <label
                        key={optionIndex}
                        className={`flex cursor-pointer items-center gap-3 rounded-none border px-4 py-3 text-sm transition ${selected ? 'border-accent bg-accent/5 text-[#19172A]' : 'border-[#E7E5F0] text-[#19172A] hover:border-accent/40 hover:bg-[#FAF9FF]'}`}
                      >
                        <input
                          type="radio"
                          name={`q-${q.question_id}`}
                          checked={selected}
                          onChange={() => setAnswers((prev) => ({ ...prev, [q.question_id]: optionIndex }))}
                          className="sr-only"
                        />
                        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold ${selected ? 'border-accent bg-accent text-white' : 'border-[#D5D1E8] text-[#69738A]'}`}>
                          {LETTERS[optionIndex] ?? optionIndex + 1}
                        </span>
                        <span>{option}</span>
                      </label>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-[#E7E5F0] bg-white px-6 py-3">
        <div className="mx-auto flex flex-wrap items-center gap-3" style={{ maxWidth: 800 }}>
          <span className="text-sm text-[#69738A]">ตอบแล้ว {answered} / {total} ข้อ</span>
          {error && <span className="text-sm text-red-600">{error}</span>}
          <div className="flex-1" />
          {isPre && (
            <button
              onClick={skip}
              disabled={submitting}
              className="rounded-none border border-[#E7E5F0] px-4 py-2 text-sm text-[#69738A] transition hover:text-[#19172A] disabled:opacity-50"
            >
              ข้ามไปก่อน
            </button>
          )}
          <button
            onClick={submit}
            disabled={submitting || answered < total}
            className="rounded-none bg-accent px-5 py-2 text-sm font-medium text-white transition hover:bg-accent2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'กำลังส่ง...' : 'ส่งคำตอบ'}
          </button>
        </div>
      </div>
    </div>
  )
}
